import {doc} from '../../docs.js';
import {gl_uniform} from '../variables.js';
import {gl_sdf} from './scene.js';
import {lazy} from '../lazy.js';
import {extractCode} from '../extract.js';
import {sdf_union} from './sdf.js';
import {hashCode} from "utils/hashCode.js";
import {uuid} from 'uuidv4';
import {float, extract} from '../utils.js';

const SHADOW_ITER = 4;

doc(
    "gl.directionalLighting",
    {
        "Data": "",
        "lightPosition": "vec3",
        "lightAttentuation": "float",
        "lightColor": "vec4",
        "shininess": "float",
        "fresnel": "vec3",
        "diffuse": "float",
        "specular": "float",
        "ambient": "vec3",
        "shadow": "float",
    },
    "Calculates how much lighting should occur at a given ray end point",
    () => 1,
    {
        isHot: true,
        connects: [
            {
                outlet: "Output 1",
                object: "gl.fragColor",
                inlet: "Input"
            }
        ],
        inlets:10 ,
    }
);

export const manageUniforms = () => {

    let uniforms = [];

    const eat = (arg, number, defaultValue) => {
        if (arg === undefined) {
            return defaultValue;
        } else if (!isNaN(arg) || Array.isArray(arg)) {
            // then we want to create a uniform
            if (!uniforms[number]) {
                let id = uuid().replaceAll('-', '');
                let uniform = gl_uniform({}, () => "arg_" + number + id);
                uniforms[number] = uniform;
            }
            return uniforms[number]([arg], true);
        } 
        return arg;
    };

    return  (args) => {
        args = [... args].map(x => x === undefined || (isNaN(x) && Object.keys(x).length === 0) ? undefined: x).slice(0, 9);
        args[0] = eat(args[0], 0, 'vec3(0.5, 0.8, 1.0)'); // light position
        args[1] = eat(args[1], 1 , "1.0"); // light attentuation
        args[2] = eat(args[2], 2 , 'vec4(0.9, 0.8, 1.0, 1.0)'); // light color
        args[3] = eat(args[3], 3 , "1.0"); // shiniess
        args[4] = eat(args[4], 4 , 'vec3(0.5, 0.8, 1.0)'); // fresnel
        args[5] = eat(args[5], 5 , 0.8); // diffuse
        args[6] = eat(args[6], 6 , 0.8); // specular
        args[7] = eat(args[7], 7 , "vec4(0.3, 0.1, 0.2, 1.0)"); // ambient
        args[8] = eat(args[8], 8 , "1.0"); // shadow
        return args;
    };
}

export const collectUniforms = (args) => {
    let uniforms = {};

    for (let arg of args) {
        if (arg.uniforms) {
            uniforms = {
                ... uniforms,
                ... arg.uniforms
            };
        }
    }
    return uniforms;
};

export const gl_directional_lighting = (
    object,
    ... args
) => {
    let id = uuid().replaceAll('-', '');
    let functionName = "directionalLighting_" + id.slice(0,4); 
    let uniformManager = manageUniforms();
    return ([data]) => {
        let scene = data.scene;
        let {codes, variables, uniforms, functions} = extract(
            data.scene, data.occlusion, data.rayOrigin, data.rayDirection, data.rayMarchedDistance, data.normal, ...args);
        codes = [...codes.slice(0, 6), ...uniformManager(codes.slice(6))];

        uniforms = {
            ... uniforms,
            ... collectUniforms(codes)
        };

        codes = codes.map(x => x.code ? x.code : x);

        let dist = data.rayMarchedDistance;
        uniforms = {
            ... dist.uniforms,
            ... uniforms,
        };

        functions = {
            ... dist.functions,
            ... functions,
        };

        variables = {
            ... dist.variables,
            ... variables,
        };


        functions["calculateTransmittance"] = {
            args: {
                "ro": "vec3",
                "rd": "vec3",
                "tmin": "float",
                "tmax": "float",
                "atten": "float",
            },
            returnType: "vec3",
            body: `
     const int MAX_DEPTH = 4;
    float hitPoints[MAX_DEPTH];
    int depth = 0;
    
    for (float t = tmin; t < tmax;)
    {
        float h = abs(${scene.code("ro + t * rd")}.x);
        if (h < 1e-5) { hitPoints[depth++] = t; t += 0.01; };
        if (depth >= MAX_DEPTH) break;
        t += h;
    }
    
    float thickness = 0.0;
    for (int i = 0; i < depth - 1; i += 2) thickness += hitPoints[i+1] - hitPoints[i];
    
    return vec3(1.0) * exp(-atten * thickness * thickness);
`
                
        };
        let shadowName = "softShadow" + id;
        functions[shadowName] = {
            args: {
                "ro": "vec3",
                "rd": "vec3",
                "mint": "float",
                "tmax": "float",
                "k": "float",
            },
            returnType: "float",
            body: `
         float res = 1.0;
        float t = mint;

        for( int i = 0; i < ${SHADOW_ITER}; i++ ) {
          float h = ${scene.code('ro + rd * t')}.x;
          res = min( res, k * h / t );
          t += clamp( h, 0.02, 0.10 );
          if( h<0.001 || t>tmax ) break;
        }

        return clamp( res, 0.0, 1.0 );
`
        };

        functions[functionName] = {
            args: {
                "occlusion": "float",
                "rayOrigin": "vec3",
                "rayDirection": "vec3",
                "distance": "float",
                "normal": "vec3",
                "lightPosition": "vec3",
                "lightAttenuation": "float",
                "lightColorRaw": "vec4",
                "shininess": "float",
                "fresnel": "vec3",
                "diffuse": "float",
                "specular": "float",
                "ambientRaw": "vec4",
                "shadow": "float",
            },
            returnType: "vec4",
            body: `
  vec3 lightColor = vec3(lightColorRaw.x, lightColorRaw.y, lightColorRaw.z);
  vec3 ambient = vec3(ambientRaw.x, ambientRaw.y, ambientRaw.z);
  vec3 surfacePosition = rayOrigin + rayDirection*distance;
  vec3  outputColor   = vec3( 0. );
  vec3 surfaceToLightDirection = normalize( lightPosition - surfacePosition );
  vec3 lightDir = normalize(vec3(1.0, .5, -1.0));

// todo: figure out translucency correctly opacity
/*
  float t = 0.8;
  lightColor = t * lightColor * calculateTransmittance(surfacePosition+normal*vec3(0.01), lightDir, 0.01, 10.0, 2.0*lightAttenuation);
  lightColor += (1.0 - t) * calculateTransmittance(surfacePosition+normal*vec3(0.01), rayDirection, 0.01, 10.0, 0.5*lightAttenuation);
*/

  // get similarity between normal and direction to light
  float diffuseCoefficient = dot( normal, surfaceToLightDirection ); 
  // get reflection angle for light striking surface
  vec3 angleOfReflection = reflect( -surfaceToLightDirection, normal );
  // see if reflected light travels to camera and generate coefficient accordingly
  float specularAngle = clamp( dot( angleOfReflection, -rayDirection ), 0., 1. );
  float specularCoefficient = pow( specularAngle, shininess );
  // lights should have an attenuation factor
  float attenuation = 1. / ( lightAttenuation * pow( length( lightPosition - surfacePosition ), 2. ) ); 
  // bias, scale, power
  float _fresnel = fresnel.x + fresnel.y * pow( 1.0 + dot( rayDirection, normal ), fresnel.z ); 
  // shadow was here
  diffuseCoefficient *= ${shadowName}( surfacePosition, normalize( lightPosition ), 0.02, 2.5, shadow);
  vec3 color = vec3( 0. );
  color += 1.2 * diffuseCoefficient * diffuse * lightColor;
  color += 2.2 * specularCoefficient * specular * lightColor;
  color += 0.3 * (ambient * lightColor) * occlusion;
  color += (_fresnel * lightColor);
  // texture
  // gamma correction must occur before light attenuation
  // which means it must be applied on a per-light basis unfortunately
  vec3 gammaCorrectedColor = pow( color, vec3( 1./2.2 ) );
  vec3 attenuatedColor = 2. * gammaCorrectedColor * attenuation; 
  outputColor += attenuatedColor;
  return vec4(outputColor.x, outputColor.y, outputColor.z, 1.0);
`
        };

        let code = `${functionName}(\n\t${codes.slice(1).join(',\n\t')
})`;
        return {
            code,
            functions,
            uniforms,
            variables
        };
    };
};
 

export const lighting = {
    "gl.directionalLighting": gl_directional_lighting
};
